import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import { BooleanInput } from '@angular/cdk/coercion';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { CdkPortal, DomPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import {
  afterNextRender,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroupDirective, NgControl } from '@angular/forms';
import { LucideCalendar, LucideDynamicIcon } from '@lucide/angular';
import { NgpInput } from 'ng-primitives/input';
import { ChangeFn, provideValueAccessor, TouchedFn } from 'ng-primitives/utils';
import { filter, takeUntil } from 'rxjs';
import { DatePickerComponent } from '../../organisms/dates/date-picker/date-picker.component';
import { TextColor, TextDirective } from '../../typography/text.directive';
import {
  SelectPortalContainerId,
  SelectPortalContainerService,
} from '../../utils/select-portal-container/select-portal-container.service';
import {
  formatDateBrowserLocaleString,
  formatDateDisplayString,
  formatLocalDateString,
  normalizeDateLocale,
  parseDateDisplayString,
  parseLocalDateString,
} from './input-date.utils';
import { JoiiIcon } from '../../icons/lucide/icon';

export type JoiiInputValue = string | number;
export type JoiiInputVariant = 'default' | 'multiline' | 'calendar';

const CALENDAR_OVERLAY_POSITIONS: ConnectedPosition[] = [
  {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
    offsetY: 4,
  },
  {
    originX: 'start',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'bottom',
    offsetY: -4,
  },
];

@Component({
  selector: 'joii-input',
  imports: [NgpInput, TextDirective, LucideDynamicIcon, CdkPortal, DatePickerComponent],
  providers: [provideValueAccessor(InputComponent)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (prefixIcon(); as prefixIcon) {
      <svg [lucideIcon]="prefixIcon" [size]="24" (click)="prefixIconClick.emit()" />
    }
    <label
      ngpLabel
      joii-text
      [color]="labelColor()"
      [size]="isFloating() ? 2 : 5"
      [attr.data-has-prefix-icon]="prefixIcon() ? 'true' : null"
      [attr.data-floating]="isFloating()"
      [attr.data-multiline]="variant() === 'multiline' ? 'true' : null"
      [for]="inputId()"
      >{{ labelText() }}
      @if (labelMarker() === 'required') {
        <span class="joii-input-label-marker" joii-text color="error" [size]="isFloating() ? 2 : 5" aria-hidden="true"
          >*</span
        >
      } @else if (labelMarker() === 'optional') {
        <span class="joii-input-label-marker" joii-text color="inline-link" [size]="isFloating() ? 2 : 5"
          >(optional)</span
        >
      }
    </label>
    @if (variant() === 'multiline') {
      <div class="joii-input-multiline-body">
        <textarea
          class="joii-input-textarea"
          joii-text
          [color]="readonly() || disabled() ? 'placeholder' : 'form-text-active'"
          [id]="inputId()"
          [attr.data-automationid]="automationId() ?? null"
          [value]="nativeInputValue()"
          [attr.rows]="rows()"
          [attr.placeholder]="placeholder() || null"
          [disabled]="disabled() || formDisabled()"
          [attr.readonly]="readonly() ? '' : null"
          [attr.aria-label]="ariaLabel() || label() || ''"
          [attr.aria-describedby]="ariaDescribedby() || null"
          [attr.aria-invalid]="hasError"
          [attr.aria-required]="labelRequirement() === 'required' || null"
          (input)="onTextareaInput($event)"
          (focus)="onFocus()"
          (blur)="onBlur()"
        ></textarea>
      </div>
    } @else {
      @if (prefixText()) {
        <span class="joii-input-affix-text" joii-text [size]="5" color="tint2" [id]="prefixTextId()">{{
          prefixText()
        }}</span>
      }
      <input
        #inputRef
        ngpInput
        joii-text
        [color]="readonly() || disabled() ? 'placeholder' : 'form-text-active'"
        [id]="inputId()"
        [attr.data-automationid]="automationId() ?? null"
        [value]="nativeInputValue()"
        [type]="nativeInputType()"
        [attr.min]="numberMinAttribute() ?? dateMinAttribute() ?? null"
        [attr.max]="numberMaxAttribute() ?? dateMaxAttribute() ?? null"
        [disabled]="disabled() || formDisabled()"
        [attr.readonly]="isInputReadonly() ? '' : null"
        [attr.aria-label]="ariaLabel() || label() || ''"
        [attr.aria-describedby]="inputDescribedBy()"
        [attr.aria-invalid]="hasError"
        [attr.aria-required]="labelRequirement() === 'required' || null"
        [attr.aria-haspopup]="isCalendarVariant() ? 'dialog' : null"
        [attr.aria-expanded]="isCalendarVariant() ? calendarOverlayOpen() : null"
        [attr.aria-controls]="calendarOverlayOpen() ? calendarDialogId() : null"
        [autocomplete]="autocomplete()"
        [attr.placeholder]="nativePlaceholder()"
        (input)="onInput($event)"
        (keydown)="onKeydown($event)"
        (click)="onDateInputClick($event)"
        (focus)="onDateInputFocus()"
        (blur)="onDateInputBlur($event)"
      />
      @if (suffixText()) {
        <span
          class="joii-input-affix-text joii-input-suffix-text"
          joii-text
          [size]="5"
          color="tint2"
          [id]="suffixTextId()"
          >{{ suffixText() }}</span
        >
      }
    }
    @if (variant() !== 'multiline' && type() === 'date') {
      <svg class="joii-input-date-trigger" [lucideIcon]="calendarIcon" [size]="24" (click)="openDatePicker()" />
    } @else if (variant() !== 'multiline' && suffixIcon()) {
      <button
        type="button"
        class="joii-input-suffix-button"
        [attr.aria-label]="suffixIconAriaLabel() || null"
        [disabled]="disabled() || formDisabled() || readonly()"
        (click)="suffixIconClick.emit()"
      >
        <svg [lucideIcon]="suffixIcon() ?? null" [size]="24" />
      </button>
    }

    <div #calendarFooterHost class="joii-input-calendar-footer-host">
      <ng-content select="[slot=calendar-footer]" />
    </div>

    <ng-template #datePickerPortal="cdkPortal" cdkPortal>
      <div
        class="joii-input-calendar-container"
        role="dialog"
        aria-modal="true"
        [id]="calendarDialogId()"
        [attr.aria-label]="calendarDialogLabel()"
        [class.has-footer]="hasCalendarFooter()"
        (focusout)="onCalendarFocusOut($event)"
      >
        <joii-date-picker
          [date]="calendarSelectedDate()"
          [min]="calendarMinDate()"
          [max]="calendarMaxDate()"
          [disabled]="disabled() || formDisabled()"
          (dateChange)="onCalendarDateChange($event)"
        />
        @if (hasCalendarFooter()) {
          <div class="joii-input-calendar-footer"></div>
        }
      </div>
    </ng-template>
  `,
  styles: `
    :host {
      position: relative;
      display: flex;
      align-items: center;
      gap: var(--Vetai-Spacing-2);
      height: var(--joii-input-height);
      border-radius: var(--Vetai-Radius-2);
      padding: var(--Vetai-Spacing-3);
      border: 1px solid var(--Vetai-Form_controls--Border_Default);
      background-color: var(--Vetai-Form_controls--Background_Default);
      outline: none;
      width: 100%;
    }
    .joii-input-label-marker {
      margin-left: var(--Vetai-Spacing-1);
    }
    .joii-input-affix-text {
      flex: none;
      white-space: nowrap;
    }
    :host([data-fixed-width='true']) {
      width: var(--joii-input-width);
    }
    :host([data-full-height='true']) {
      height: 100%;
    }
    :host([data-fixed-height='true']) {
      height: var(--joii-input-height);
    }
    :host([data-focused='true']),
    :host([data-hovered='true']) {
      background-color: var(--Vetai-Form_controls--Background_Hover);
      border: 1px solid var(--Vetai-Form_controls--Border_Hover);
    }
    :host([data-active='true'][data-hovered='false']),
    :host([data-active='true'][data-focused='false']) {
      border: 1px solid var(--Vetai-Form_controls--Border_Hover);
    }
    :host([data-active='true'][data-focused='true']) {
      background-color: var(--Vetai-Form_controls--Background_Hover);
      border: 1px solid var(--Vetai-Form_controls--Border_Active);
    }

    :host([data-success='true']) {
      border: 1px solid var(--Vetai-Status_packages--Success--Default);
    }

    :host([data-success='true'][data-focused='true']),
    :host([data-success='true'][data-hovered='true']) {
      border: 1px solid var(--Vetai-Status_packages--Success--Hover);
    }

    :host([data-success='true'][data-active='true']) {
      border: 1px solid var(--Vetai-Status_packages--Success--Active);
    }
    :host([data-error='true']) {
      border: 1px solid var(--Vetai-Status_packages--Error--Default);
    }

    :host([data-error='true'][data-focused='true']),
    :host([data-error='true'][data-hovered='true']) {
      border: 1px solid var(--Vetai-Status_packages--Error--Hover);
    }

    :host([data-error='true'][data-active='true']) {
      border: 1px solid var(--Vetai-Status_packages--Error--Active);
    }

    :host([data-readonly='true']),
    :host([data-readonly='true'][data-focused='true']),
    :host([data-readonly='true'][data-hovered='true']),
    :host([data-readonly='true'][data-active='true']),
    :host([data-disabled='true']),
    :host([data-disabled='true'][data-focused='true']),
    :host([data-disabled='true'][data-hovered='true']),
    :host([data-disabled='true'][data-active='true']) {
      border: 1px solid var(--Vetai-Form_controls--Border_Default);
      background-color: var(--Vetai-Backgrounds--Tint_2);
      cursor: not-allowed;
    }

    [ngpInput],
    [ngpInput]:focus {
      position: relative;
      top: var(--Vetai-Spacing-0-5);
      flex: 1;
      border: none;
      outline: none;
      background: transparent;
      box-shadow: none;
      padding: 0;
      align-self: flex-end;
      min-width: 0;
      flex-shrink: 1;
    }
    /* Without a label there is no floating label to leave room for, so centre the text vertically. */
    :host([data-has-label='false']) [ngpInput] {
      top: 0;
      align-self: center;
    }
    :host([data-has-label='false']) [ngpLabel] {
      display: none;
    }
    [ngpInput]::placeholder {
      color: var(--Vetai-Form_controls--Placeholder);
      opacity: 1;
    }
    :host([data-type='date']) [ngpInput]::-webkit-calendar-picker-indicator,
    :host([data-type='date']) [ngpInput]::-webkit-inner-spin-button {
      display: none;
      -webkit-appearance: none;
    }
    :host([data-type='date']) [ngpInput]::-webkit-date-and-time-value {
      text-align: left;
    }
    :host([data-variant='calendar']) [ngpInput] {
      cursor: pointer;
    }
    .joii-input-date-trigger {
      cursor: pointer;
    }
    .joii-input-suffix-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin: 0;
      padding: 0;
      border: none;
      background: transparent;
      color: inherit;
      cursor: pointer;
      border-radius: var(--Vetai-Radius-1);
    }
    .joii-input-suffix-button:focus {
      outline: none;
    }
    .joii-input-suffix-button:focus-visible {
      outline: 2px solid var(--Vetai-Inline_links_and_info--Default);
      outline-offset: 2px;
    }
    :host([data-readonly='true']) .joii-input-date-trigger,
    :host([data-disabled='true']) .joii-input-date-trigger,
    :host([data-readonly='true']) .joii-input-suffix-button,
    :host([data-disabled='true']) .joii-input-suffix-button {
      cursor: not-allowed;
    }
    :host([data-readonly='true']) [ngpInput],
    :host([data-readonly='true']) [ngpLabel],
    :host([data-disabled='true']) [ngpLabel],
    :host([data-disabled='true']) [ngpInput] {
      cursor: not-allowed;
    }
    [ngpLabel] {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      transition: all 0.2s ease-in-out;
      padding: var(--Vetai-Spacing-0);
    }
    [ngpLabel][data-has-prefix-icon='true'] {
      padding: 0 12px 0 32px;
    }
    [ngpLabel][data-floating='true'] {
      top: 14px;
    }
    [ngpLabel][data-floating='false'] {
      top: 50%;
    }
    svg.lucide {
      color: var(--Vetai-Form_controls--Text_Default);
    }
    :host[data-focused='true'] svg.lucide {
      color: var(--Vetai-Form_controls--Text_Hover);
    }
    :host[data-readonly='true'] svg.lucide,
    :host[data-disabled='true'] svg.lucide {
      color: var(--Vetai-Form_controls--Placeholder);
    }

    :host([data-variant='multiline']) {
      flex-direction: column;
      align-items: stretch;
      min-height: var(--joii-input-height);
      height: auto;
      overflow: hidden;
    }
    :host([data-variant='multiline']) .joii-input-multiline-body {
      width: 100%;
      flex: 0 0 auto;
      min-height: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      padding-top: var(--Vetai-Spacing-0-5);
    }
    :host([data-variant='multiline']) [ngpLabel][data-multiline='true'] {
      top: 14px;
    }
    .joii-input-textarea {
      flex: 1;
      width: 100%;
      min-width: 0;
      border: none;
      outline: none;
      background: transparent;
      box-shadow: none;
      padding: 0;
      margin-top: var(--Vetai-Spacing-0-5);
      resize: vertical;
      overflow-y: auto;
    }
    /* Multiline: CSS auto-grow (same approach as joii-input-llm); max-height caps then scrolls. */
    :host([data-variant='multiline']) .joii-input-textarea {
      flex: 0 0 auto;
      margin-top: 0;
      field-sizing: content;
      min-height: calc(var(--joii-input-multiline-rows, 2) * 1lh);
      max-height: var(--joii-input-multiline-max, 300px);
      align-self: stretch;
      box-sizing: border-box;
      overflow-x: hidden;
      overflow-y: auto;
      overflow-wrap: anywhere;
      word-break: break-word;
      resize: none;
    }
    .joii-input-textarea::placeholder {
      color: var(--Vetai-Form_controls--Placeholder);
    }
    :host([data-readonly='true']) .joii-input-textarea,
    :host([data-disabled='true']) .joii-input-textarea {
      cursor: not-allowed;
    }
    .joii-input-calendar-container {
      position: relative;
      overflow: visible;
    }
    .joii-input-calendar-footer-host {
      display: none;
    }
    .joii-input-calendar-container.has-footer {
      display: flex;
      flex-direction: column;
      background-color: var(--Vetai-Backgrounds--Tint_1);
      border-radius: var(--Vetai-Radius-2);
      box-shadow: var(--ngp-shadow);
      border: 1px solid var(--ngp-border);
    }
    .joii-input-calendar-container.has-footer joii-date-picker {
      width: 100%;
      box-sizing: border-box;
      background-color: transparent;
      border: none;
      box-shadow: none;
      border-radius: 0;
    }
    .joii-input-calendar-footer {
      padding: var(--Vetai-Spacing-2) var(--Vetai-Spacing-3) var(--Vetai-Spacing-3);
      border-top: 1px solid var(--ngp-border);
    }
    .joii-input-calendar-footer .joii-input-calendar-footer-host {
      display: block;
    }
  `,
  host: {
    '[attr.data-has-label]': 'label() ? "true" : "false"',
    '[attr.data-focused]': 'focused()',
    '[attr.data-active]': 'isActive()',
    '[attr.data-hovered]': 'isHovered()',
    '[attr.data-error]': 'hasError ? "true" : null',
    '[attr.data-readonly]': 'readonly() ? "true" : null',
    '[attr.data-disabled]': 'disabled() ? "true" : null',
    '[attr.data-success]': 'successMessage() ? "true" : null',
    '[attr.data-fixed-width]': 'fixedWidth() ? "true" : null',
    '[style.--joii-input-width]': `inputWidth() + 'px'`,
    '[attr.data-full-height]': 'fullHeight() ? "true" : null',
    '[attr.data-fixed-height]': 'fixedHeight() ? "true" : null',
    '[style.--joii-input-height]': `inputHeight() + 'px'`,
    '[style.--joii-input-multiline-max]': 'variant() === "multiline" ? maxHeight() + "px" : null',
    '[style.--joii-input-multiline-rows]': 'variant() === "multiline" ? rows() : null',
    '[attr.data-type]': 'type()',
    '[attr.data-variant]': 'variant()',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
  },
})
export class InputComponent {
  readonly label = input<string>('');
  readonly placeholder = input<string>('');
  readonly autocomplete = input<string | undefined>();
  readonly type = input<string>('text');
  /** Minimum value for native number inputs. Forwarded to the underlying input element. */
  readonly min = input<number | undefined>(undefined);
  /** Maximum value for native number inputs. Forwarded to the underlying input element. */
  readonly max = input<number | undefined>(undefined);
  /** Minimum date (`YYYY-MM-DD`) for date inputs and calendar variant. */
  readonly minDate = input<string | undefined>(undefined);
  /** Maximum date (`YYYY-MM-DD`) for date inputs and calendar variant. */
  readonly maxDate = input<string | undefined>(undefined);
  /** Locale hint for date display. Supported explicit locales: `en-GB`, `en-US`. Defaults to native browser behavior. */
  readonly dateLocale = input<string | undefined>(undefined);
  /**
   * Optional display override for the input text. When set, this string is shown
   * in the field without changing the underlying control value.
   */
  readonly displayValue = input<string | undefined>(undefined);
  /**
   * Variant: `default` for single-line input, `multiline` for textarea,
   * `calendar` for the design-system date picker overlay (requires `type="date"`).
   */
  readonly variant = input<JoiiInputVariant>('default');
  /** Number of visible rows when variant is 'multiline'. */
  readonly rows = input<number>(2);
  /** Max height in px for multiline auto-expand. */
  readonly maxHeight = input<number>(300);
  readonly inputId = input<string>(`joii-input-field-${Math.random().toString(36).substr(2, 9)}`);
  readonly automationId = input<string>();
  readonly value = model<JoiiInputValue>('');
  readonly fixedWidth = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
  readonly inputWidth = input<number>(320);
  readonly fullHeight = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
  readonly fixedHeight = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
  readonly inputHeight = input<number>(56);
  readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
  readonly readonly = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
  readonly prefixIcon = input<JoiiIcon>();
  readonly suffixIcon = input<JoiiIcon>();
  readonly suffixIconAriaLabel = input<string | undefined>();
  /**
   * Marks the label as `required` (a red asterisk, and `aria-required` on the field) or `optional`
   * (`(optional)` in the link colour). Not shown while the label is replaced by a message.
   */
  readonly labelRequirement = input<'required' | 'optional' | undefined>();
  /** Text shown before the value, e.g. `Weight` or `£`. Read out with the field. Not shown for `multiline`. */
  readonly prefixText = input<string | undefined>();
  /** A unit shown after the value, e.g. `kg` or `breaths/min`. Read out with the field. Not shown for `multiline`. */
  readonly suffixText = input<string | undefined>();
  /** Error message shown when the field is in an error state (e.g. invalid + touched + dirty). */
  readonly errorMessage = input<string | undefined>();
  readonly successMessage = input<string | undefined>();

  readonly ariaLabel = input<string | undefined>();
  readonly ariaDescribedby = input<string | undefined>();

  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formDirective = inject(FormGroupDirective, { optional: true });
  private readonly overlay = inject(Overlay);
  private readonly scrollDispatcher = inject(ScrollDispatcher);
  private readonly selectPortalContainer = inject(SelectPortalContainerService);
  private readonly focusTrapFactory = inject(FocusTrapFactory);

  private overlayRef: OverlayRef | null = null;
  private calendarPortalContainerId: SelectPortalContainerId | null = null;
  private calendarFooterPortalOutlet: DomPortalOutlet | null = null;
  private calendarFocusTrap: FocusTrap | null = null;
  /** Set while focus is moved back to the input so that the focus doesn't reopen the calendar. */
  private restoringInputFocus = false;

  /** Signal that updates when the attached control's validation status or value changes. Resolved lazily to avoid NG0200 circular DI. */
  private readonly controlStatusVersion = signal(0);

  constructor() {
    afterNextRender(() => {
      const ngControl = this.injector.get(NgControl, null);
      const control = ngControl?.control;
      if (!control) return;
      const bumpVersion = () => this.controlStatusVersion.update((v) => v + 1);
      control.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(bumpVersion);
      control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(bumpVersion);
      this.formDirective?.ngSubmit.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(bumpVersion);
    });

    effect(() => {
      if (this.disabled() || this.formDisabled() || this.readonly()) {
        this.closeCalendarOverlay();
      }
    });

    this.destroyRef.onDestroy(() => this.closeCalendarOverlay());
  }

  protected readonly formDisabled = signal(false);
  protected readonly isHovered = signal(false);
  protected readonly calendarOverlayOpen = signal(false);

  inputRef = viewChild<ElementRef<HTMLInputElement>>('inputRef');
  datePickerPortal = viewChild.required(CdkPortal);
  private readonly datePicker = viewChild(DatePickerComponent);
  private readonly calendarFooterHost = viewChild<ElementRef<HTMLElement>>('calendarFooterHost');

  readonly prefixIconClick = output<void>();
  readonly suffixIconClick = output<void>();
  /** Emits when Enter is pressed in the input. */
  readonly enterKeypress = output<void>();

  private onChange?: ChangeFn<JoiiInputValue | undefined>;
  protected onTouched?: TouchedFn;
  private readonly dateInputDraft = signal<string | null>(null);

  readonly resolvedDateLocale = computed(() => normalizeDateLocale(this.dateLocale()));

  readonly usesCalendarLocaleFormatting = computed(
    () => this.type() === 'date' && this.variant() === 'calendar' && this.resolvedDateLocale() !== 'native'
  );

  readonly prefixTextId = computed(() => `${this.inputId()}-prefix`);
  readonly suffixTextId = computed(() => `${this.inputId()}-suffix`);
  readonly inputDescribedBy = computed(
    () =>
      [
        this.ariaDescribedby(),
        this.prefixText() ? this.prefixTextId() : null,
        this.suffixText() ? this.suffixTextId() : null,
      ]
        .filter(Boolean)
        .join(' ') || null
  );
  readonly nativeInputType = computed(() => {
    // The calendar variant is read-only and picks dates through the overlay, so a native date input
    // would only add its day/month/year segments, which swallow Tab and trap keyboard users.
    if (this.usesCalendarLocaleFormatting() || this.isCalendarVariant()) {
      return 'text';
    }
    return this.type();
  });

  readonly nativeInputValue = computed(() => {
    const displayValue = this.displayValue();
    if (displayValue) {
      return displayValue;
    }

    const draft = this.dateInputDraft();
    if (draft !== null && this.usesCalendarLocaleFormatting()) {
      return draft;
    }

    const v = this.value();
    if (v === null || v === undefined) {
      return '';
    }

    if (this.usesCalendarLocaleFormatting() && typeof v === 'string') {
      return formatDateDisplayString(v, this.dateLocale());
    }

    if (this.isCalendarVariant() && typeof v === 'string') {
      return formatDateBrowserLocaleString(v);
    }

    return String(v);
  });

  readonly numberMinAttribute = computed(() => (this.type() === 'number' ? this.min() : undefined));
  readonly numberMaxAttribute = computed(() => (this.type() === 'number' ? this.max() : undefined));
  readonly dateMinAttribute = computed(() =>
    this.type() === 'date' && this.nativeInputType() === 'date' ? this.minDate() : undefined
  );
  readonly dateMaxAttribute = computed(() =>
    this.type() === 'date' && this.nativeInputType() === 'date' ? this.maxDate() : undefined
  );

  readonly calendarSelectedDate = computed(() => {
    const value = this.value();
    return typeof value === 'string' ? parseLocalDateString(value) : undefined;
  });

  readonly calendarMinDate = computed(() => parseLocalDateString(this.minDate()));
  readonly calendarMaxDate = computed(() => parseLocalDateString(this.maxDate()));

  protected readonly calendarDialogId = computed(() => `${this.inputId()}-calendar`);
  protected readonly calendarDialogLabel = computed(() => {
    const label = this.ariaLabel() || this.label();
    return label ? `Choose ${label}` : 'Choose date';
  });

  protected isCalendarVariant(): boolean {
    return this.type() === 'date' && this.variant() === 'calendar';
  }

  protected readonly hasCalendarFooter = computed(
    () => (this.calendarFooterHost()?.nativeElement.childElementCount ?? 0) > 0
  );

  protected isInputReadonly(): boolean {
    return this.readonly() || this.isCalendarVariant();
  }

  writeValue(value: JoiiInputValue | null | undefined): void {
    this.dateInputDraft.set(null);
    this.value.set(value === null || value === undefined ? '' : value);
  }
  registerOnChange(fn: ChangeFn<JoiiInputValue | undefined>): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: TouchedFn): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }
  protected onValueChange(value: JoiiInputValue): void {
    this.onChange?.(value);
  }
  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (this.usesCalendarLocaleFormatting()) {
      this.onLocalizedDateInput(target);
      return;
    }

    const value = this.parseInputValue(target.value);
    this.value.set(value);
    this.onValueChange(value);
    // If the parsed/clamped value differs from what the user typed, push it
    // back into the DOM so the native input stays in sync.
    if (typeof value === 'number') {
      const asStr = String(value);
      if (target.value !== asStr) {
        target.value = asStr;
      }
    }
  }

  private parseInputValue(raw: string): JoiiInputValue {
    if (this.type() === 'date') {
      return raw;
    }

    if (this.type() !== 'number') {
      return raw;
    }
    if (raw === '') {
      return '';
    }
    const num = Number(raw);
    return !isNaN(num) ? this.clampNumber(num) : raw;
  }

  private clampNumber(num: number): number {
    const minValue = this.min();
    const maxValue = this.max();
    if (minValue !== undefined && num < minValue) {
      return minValue;
    }
    if (maxValue !== undefined && num > maxValue) {
      return maxValue;
    }
    return num;
  }

  private onLocalizedDateInput(target: HTMLInputElement): void {
    const raw = target.value;
    this.dateInputDraft.set(raw);

    if (raw.trim() === '') {
      this.value.set('');
      this.onValueChange('');
      return;
    }

    const parsed = parseDateDisplayString(raw, this.dateLocale());
    if (!parsed) {
      return;
    }

    const clamped = this.clampDate(parsed);
    this.value.set(clamped);
    this.onValueChange(clamped);

    const formatted = formatDateDisplayString(clamped, this.dateLocale());
    this.dateInputDraft.set(formatted);
    if (target.value !== formatted) {
      target.value = formatted;
    }
  }

  private clampDate(value: string): string {
    const date = parseLocalDateString(value);
    if (!date) {
      return value;
    }

    const min = this.minDate();
    if (min && parseLocalDateString(min) && value < min) {
      return min;
    }

    const max = this.maxDate();
    if (max && parseLocalDateString(max) && value > max) {
      return max;
    }

    return value;
  }

  private normalizeNumberValue(): void {
    const current = this.value();
    if (current === '') {
      return;
    }
    const num = typeof current === 'number' ? current : Number(current);
    if (isNaN(num)) {
      return;
    }
    const clamped = this.clampNumber(num);
    if (clamped !== num) {
      this.value.set(clamped);
      this.onValueChange(clamped);
      // Sync the clamped value back into the native input so the DOM matches
      const nativeInput = this.inputRef()?.nativeElement;
      if (nativeInput) {
        nativeInput.value = String(clamped);
      }
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.enterKeypress.emit();
    }
    if (this.isCalendarVariant() && this.onCalendarInputKeydown(event)) {
      return;
    }
    // Block scientific-notation characters that browsers allow in number inputs
    // but that are not useful in standard numeric form fields.
    if (this.type() === 'number' && (event.key === 'e' || event.key === 'E' || event.key === '+')) {
      event.preventDefault();
    }
  }

  onTextareaInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    const value = target.value;
    this.value.set(value);
    this.onValueChange(value);
  }

  onFocus(): void {
    this.focused.set(true);
    this.onTouched?.();
  }

  protected onDateInputFocus(): void {
    this.onFocus();
    if (
      this.restoringInputFocus ||
      !this.isCalendarVariant() ||
      this.disabled() ||
      this.formDisabled() ||
      this.readonly()
    ) {
      return;
    }
    this.openCalendarOverlay();
  }

  /**
   * Keyboard access to the calendar dialog, following the date picker combobox pattern: Space and
   * ArrowDown open it (if needed) and move focus to the focused day, while Tab and Shift+Tab close it
   * and move on, so focus can travel between neighbouring pickers (e.g. a start and an end date).
   * Returns true when the event was handled.
   */
  private onCalendarInputKeydown(event: KeyboardEvent): boolean {
    if (this.disabled() || this.formDisabled() || this.readonly()) {
      return false;
    }
    if (event.key === 'Tab') {
      this.closeCalendarOverlay();
      return false;
    }
    if (event.key !== ' ' && event.key !== 'ArrowDown') {
      return false;
    }
    event.preventDefault();
    this.openCalendarOverlay();
    this.focusCalendarDay();
    return true;
  }

  /** Moves focus to the date picker's focused day (the one cell in the grid's tab sequence). */
  private focusCalendarDay(): void {
    // Wait for the next render's read phase: a calendar attached in this same event has rendered its
    // days, but they only become focusable once the date picker's after-render effects have run.
    afterNextRender({ read: () => this.datePicker()?.focusActiveDate() }, { injector: this.injector });
  }

  protected onDateInputClick(event: MouseEvent): void {
    if (!this.isCalendarVariant() || this.disabled() || this.formDisabled() || this.readonly()) {
      return;
    }
    event.preventDefault();
    this.openCalendarOverlay();
  }

  protected onDateInputBlur(event: FocusEvent): void {
    this.closeCalendarIfFocusLeft(event.relatedTarget);
    this.onBlur();
  }

  protected onCalendarFocusOut(event: FocusEvent): void {
    this.closeCalendarIfFocusLeft(event.relatedTarget);
  }

  /**
   * Closes the calendar when focus moves to something other than the input or the calendar, e.g. to
   * another picker. A null target (a click on something unfocusable, or leaving the window) is left
   * to the outside-click handling, so clicks inside the calendar still land.
   */
  private closeCalendarIfFocusLeft(target: EventTarget | null): void {
    if (!this.overlayRef || !(target instanceof Element)) {
      return;
    }
    const stillInside =
      target === this.inputRef()?.nativeElement ||
      this.overlayRef.overlayElement.contains(target) ||
      !!target.closest('.joii-dropdown-panel');
    if (!stillInside) {
      this.closeCalendarOverlay();
    }
  }

  onBlur(): void {
    this.focused.set(false);
    if (this.type() === 'number' && this.variant() !== 'multiline') {
      this.normalizeNumberValue();
    }
    if (this.usesCalendarLocaleFormatting() && this.variant() !== 'multiline') {
      this.normalizeForcedDateValue();
    }
    this.onTouched?.();
  }

  private normalizeForcedDateValue(): void {
    const current = this.value();
    if (typeof current !== 'string') {
      this.dateInputDraft.set(null);
      return;
    }

    if (current === '') {
      this.dateInputDraft.set(null);
      return;
    }

    const parsed = parseLocalDateString(current);
    if (!parsed) {
      this.value.set('');
      this.onValueChange('');
      this.dateInputDraft.set(null);
      return;
    }

    const canonical = formatLocalDateString(parsed);
    const clamped = this.clampDate(canonical);
    if (clamped !== current) {
      this.value.set(clamped);
      this.onValueChange(clamped);
    }

    this.dateInputDraft.set(null);
    const nativeInput = this.inputRef()?.nativeElement;
    if (nativeInput) {
      nativeInput.value = this.nativeInputValue();
    }
  }

  onMouseEnter(): void {
    this.isHovered.set(true);
  }
  onMouseLeave(): void {
    this.isHovered.set(false);
  }

  protected openDatePicker(): void {
    if (this.disabled() || this.formDisabled() || this.readonly()) {
      return;
    }
    if (this.isCalendarVariant()) {
      this.openCalendarOverlay();
      return;
    }
    const input = this.inputRef()?.nativeElement;
    if (input?.showPicker) {
      input.showPicker();
    } else {
      input?.focus();
    }
  }

  protected onCalendarDateChange(date: Date | undefined): void {
    if (!date) {
      return;
    }
    const formatted = formatLocalDateString(date);
    this.dateInputDraft.set(null);
    this.value.set(formatted);
    this.onValueChange(formatted);
    this.onTouched?.();
    this.closeCalendarOverlay({ restoreFocus: true });
  }

  private openCalendarOverlay(): void {
    if (!this.isCalendarVariant() || this.overlayRef?.hasAttached()) {
      return;
    }

    const portal = this.datePickerPortal();
    const originElement = this.inputRef()?.nativeElement;
    if (!originElement) {
      return;
    }
    const scrollableAncestors = this.scrollDispatcher.getAncestorScrollContainers(originElement);
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(originElement)
      .withPositions(CALENDAR_OVERLAY_POSITIONS)
      .withScrollableContainers(scrollableAncestors)
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition({ scrollThrottle: 0 }),
      hasBackdrop: false,
      panelClass: 'joii-input-calendar-panel',
    });

    this.overlayRef.attach(portal);
    this.calendarOverlayOpen.set(true);
    this.attachCalendarFooter(this.overlayRef.overlayElement);

    const calendarContainer = this.overlayRef.overlayElement.querySelector(
      '.joii-input-calendar-container'
    ) as HTMLElement | null;
    if (calendarContainer) {
      this.calendarPortalContainerId = this.selectPortalContainer.register(calendarContainer);
      this.calendarFocusTrap = this.focusTrapFactory.create(calendarContainer);
    }

    this.overlayRef.updatePosition();
    requestAnimationFrame(() => this.overlayRef?.updatePosition());

    const overlayDetached$ = this.overlayRef.detachments();

    this.overlayRef
      .keydownEvents()
      .pipe(
        takeUntil(overlayDetached$),
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event.key === 'Escape')
      )
      .subscribe(() => this.closeCalendarOverlay({ restoreFocus: true }));

    this.overlayRef
      .outsidePointerEvents()
      .pipe(
        takeUntil(overlayDetached$),
        takeUntilDestroyed(this.destroyRef),
        filter((event) => !this.shouldIgnoreOutsidePointerEvent(event))
      )
      .subscribe(() => this.closeCalendarOverlay());
  }

  private shouldIgnoreOutsidePointerEvent(event: MouseEvent): boolean {
    const target = event.target;
    return target instanceof Element && !!target.closest('.joii-dropdown-panel');
  }

  private attachCalendarFooter(overlayElement: HTMLElement): void {
    const footerHost = this.calendarFooterHost()?.nativeElement;
    const footerOutlet = overlayElement.querySelector('.joii-input-calendar-footer') as HTMLElement | null;
    if (!footerHost || !footerOutlet || footerHost.childElementCount === 0) {
      return;
    }

    this.calendarFooterPortalOutlet = new DomPortalOutlet(footerOutlet);
    this.calendarFooterPortalOutlet.attach(new DomPortal(footerHost));
  }

  private detachCalendarFooter(): void {
    this.calendarFooterPortalOutlet?.detach();
    this.calendarFooterPortalOutlet = null;
  }

  /**
   * @param restoreFocus when true and focus is inside the calendar, focus goes back to the input
   * (Escape or a date being picked), as the dialog pattern expects.
   */
  private closeCalendarOverlay({ restoreFocus = false }: { restoreFocus?: boolean } = {}): void {
    const focusWasInCalendar =
      restoreFocus &&
      !!this.overlayRef?.overlayElement.contains(this.overlayRef.overlayElement.ownerDocument.activeElement);
    this.detachCalendarFooter();
    if (this.calendarPortalContainerId) {
      this.selectPortalContainer.unregister(this.calendarPortalContainerId);
      this.calendarPortalContainerId = null;
    }
    this.calendarFocusTrap?.destroy();
    this.calendarFocusTrap = null;
    if (!this.overlayRef) {
      this.calendarOverlayOpen.set(false);
      return;
    }
    this.overlayRef.dispose();
    this.overlayRef = null;
    this.calendarOverlayOpen.set(false);
    if (focusWasInCalendar) {
      this.focusInputWithoutReopening();
    }
  }

  private focusInputWithoutReopening(): void {
    const input = this.inputRef()?.nativeElement;
    if (!input) {
      return;
    }
    this.restoringInputFocus = true;
    try {
      input.focus();
    } finally {
      this.restoringInputFocus = false;
    }
  }

  readonly calendarIcon = LucideCalendar;
  readonly focused = signal(false);

  /**
   * Placeholder for the single-line input. When there is a label, the label sits where the placeholder
   * would until it floats, so the placeholder only shows once the label has floated out of the way.
   */
  readonly nativePlaceholder = computed(() => {
    const placeholder = this.placeholder();
    if (!placeholder) return null;
    return !this.label() || this.isFloating() ? placeholder : null;
  });

  readonly isFloating = computed(() => {
    const isDateType = this.type() === 'date';
    // Prefix and suffix text sit on the label's line, so the label stays above them.
    const hasAffixText = this.variant() !== 'multiline' && !!(this.prefixText() || this.suffixText());
    return this.hasDisplayValue() || this.focused() || isDateType || hasAffixText;
  });

  readonly isActive = computed(() => this.hasDisplayValue());

  private hasDisplayValue(): boolean {
    if (this.displayValue()) {
      return true;
    }
    const v = this.value();
    return v !== null && v !== undefined && v !== '';
  }

  readonly errorId = input<string>(`joii-input-field-error-${Math.random().toString(36).substr(2, 9)}`);

  /** Error message from errorMessage input or derived from control errors. */
  readonly displayError = computed(() => {
    const msg = this.errorMessage();
    if (msg) return msg;
    this.controlStatusVersion(); // Recompute when control status or value changes
    const control = this.injector.get(NgControl, null)?.control;
    if (!control?.errors) return '';
    return this.getControlErrorMessageFromErrors(control.errors);
  });

  /** True when an error message is provided or the attached FormControl is invalid and (touched, submitted, or focused). */
  get hasError(): boolean {
    const control = this.injector.get(NgControl, null)?.control;
    const message = this.displayError();
    if (!control) return message.length > 0;
    const showError = this.getShowError(this.focused(), control, this.formDirective ?? undefined);
    return !!(control.invalid && showError && message.length > 0);
  }

  readonly labelText = computed(() => {
    this.controlStatusVersion();
    if (this.successMessage() && this.isFloating()) {
      return this.successMessage() ?? '';
    }
    if (this.hasError && this.displayError() && this.isFloating()) {
      return this.displayError() || this.label();
    }
    return this.label();
  });

  readonly labelMarker = computed(() => {
    const label = this.label();
    return label && this.labelText() === label ? this.labelRequirement() : undefined;
  });

  readonly labelColor = computed((): TextColor => {
    this.controlStatusVersion();
    if (this.successMessage() && this.isFloating()) return 'success';
    if (this.hasError && this.displayError() && this.isFloating()) return 'error';
    if (this.readonly()) return 'placeholder';
    if (this.isActive()) return 'inline-link';
    return 'placeholder';
  });

  private getControlErrorMessageFromErrors(errors: Record<string, unknown>): string {
    for (const key of Object.keys(errors)) {
      const value = errors[key];
      if (value && typeof value === 'object' && typeof (value as { message?: string }).message === 'string') {
        return (value as { message: string }).message;
      }
    }
    if (errors['email']) return 'Please enter a valid email address';
    if (errors['required'] ?? errors['requiredTrue']) return 'Field is required';
    if (errors['minlength'])
      return `Minimum length is ${(errors['minlength'] as { requiredLength: number }).requiredLength} characters`;
    if (errors['minLength'])
      return `Minimum length is ${(errors['minLength'] as { requiredLength: number }).requiredLength} characters`;
    if (errors['maxlength'])
      return `Maximum length is ${(errors['maxlength'] as { requiredLength: number }).requiredLength} characters`;
    if (errors['maxLength'])
      return `Maximum length is ${(errors['maxLength'] as { requiredLength: number }).requiredLength} characters`;
    if (errors['min']) return `Minimum value is ${(errors['min'] as { min: number }).min}`;
    if (errors['max']) return `Maximum value is ${(errors['max'] as { max: number }).max}`;
    if (errors['containsSpace']) return 'Cannot contain spaces';
    if (errors['invalidEmailFormat']) return 'Email format is not valid';
    if (errors['invalidCharacters']) return 'Contains invalid characters';
    if (errors['lettersOnly']) return 'Only letters are allowed';
    if (errors['numbersOnly']) return 'Only numbers are allowed';
    if (errors['missingSymbolOrNumber']) return 'Should contain a symbol or a number';
    if (errors['mustBeTrue']) return 'Checkbox is required';
    if (errors['notPostcodeFormat']) return 'Invalid postcode';
    return '';
  }

  private getShowError(
    focused: boolean,
    control?: AbstractControl<any, any, any>,
    formGroupDirective?: FormGroupDirective
  ): boolean {
    // For a reactive form field only show the error after submission.
    if (formGroupDirective) {
      return (
        (formGroupDirective.submitted ?? false) || (focused && (control?.dirty ?? false) && (control?.touched ?? false))
      );
    }
    return focused || (control?.touched ?? false) || (control?.dirty ?? false);
  }
}
